import React, { useEffect, useRef, useState, useCallback } from "react";
import ViewMoreButton from "./components/view-more-button/view-more-button";
import PropTypes from "prop-types";

/**
 * `ExpandableListWrapper` renders a list of items with a "View More" button.
 * It allows displaying a subset of children elements and expanding to show more.
 *
 * @param {object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child elements to be rendered in the list.
 * @param {number} [props.primaryNumRefinements=0] - The number of child elements to display initially.
 * @param {boolean} [props.isTruncatedFilterExpanded=false] - Flag to indicate if the list is expanded.
 * @param {boolean} [props.shouldDisplayViewMoreButton=false] - Flag to toggle styling for the "View More" button.
 * @param {boolean} [props.isTabletOrBelow=false] - Flag to indicate if the device is a tablet or smaller.
 * @param {object} props.buttonProps - Properties related to the "View More" button.
 * @param {object} [props.buttonProps.viewMoreButtonProps={}] - Additional props for the "View More" button.
 * @param {boolean} props.buttonProps.shouldDisplayTruncatedFilters - Flag to display truncated filters.
 * @param {string} props.buttonProps.parsedName - The name identifier for the button.
 * @param {function} props.buttonProps.toggleExpandedItems - Function to toggle the expanded state of the list.
 *
 * @returns {JSX.Element} The rendered list with an optional "View More" button.
 */
const ExpandableListWrapper = ({
  children,
  primaryNumRefinements,
  isTruncatedFilterExpanded,
  shouldDisplayViewMoreButton,
  isTabletOrBelow,
  buttonProps,
  ...rest
}) => {
  const {
    viewMoreButtonProps,
    shouldDisplayTruncatedFilters,
    parsedName,
    toggleExpandedItems,
  } = buttonProps;
  const [heights, setHeights] = useState({ maxHeight: 0, collapsedHeight: 0 });
  const accordionRef = useRef(null);

  const initialCollapsedItems = +primaryNumRefinements || children.length;

  const computeHeights = useCallback(() => {
    const { current } = accordionRef;
    if (!current || current.children.length === 0) {
      return { maxHeight: 0, collapsedHeight: 0 };
    }

    let collapsedHeight = 0;
    Array.from(current.children).some((child, index) => {
      if (index >= initialCollapsedItems) {
        return true; // Breaks the loop
      }
      const marginBottom = parseInt(
        window.getComputedStyle(child).marginBottom,
        10
      );
      collapsedHeight += child.offsetHeight + marginBottom;
      return false;
    });

    return {
      maxHeight: current.scrollHeight,
      collapsedHeight,
    };
  }, [initialCollapsedItems]);

  // Effect to compute and set heights whenever children change
  useEffect(() => {
    setHeights(computeHeights());
  }, [children, computeHeights, initialCollapsedItems]);

  const dynamicHeight = shouldDisplayViewMoreButton
    ? isTruncatedFilterExpanded
      ? { maxHeight: `${heights.maxHeight}px` }
      : { maxHeight: `${heights.collapsedHeight}px` }
    : null;

  return (
    <>
      <ul
        ref={accordionRef}
        style={dynamicHeight}
        data-testid="list-wrapper"
        {...rest}
      >
        {children}
      </ul>
      {shouldDisplayTruncatedFilters && (
        <ViewMoreButton
          isTrucatedFilterExpanded={isTruncatedFilterExpanded}
          toggleExpandedItems={() =>
            toggleExpandedItems({ panelName: parsedName })
          }
          {...viewMoreButtonProps}
        />
      )}
    </>
  );
};
// default props
ExpandableListWrapper.defaultProps = {
  isTabletOrBelow: false,
  shouldDisplayViewMoreButton: false,
  isTruncatedFilterExpanded: false,
  primaryNumRefinements: "0",
  buttonProps: {
    viewMoreButtonProps: {},
  },
};
// prop types
ExpandableListWrapper.propTypes = {
  isTabletOrBelow: PropTypes.bool,
  shouldDisplayViewMoreButton: PropTypes.bool,
  isTruncatedFilterExpanded: PropTypes.bool,
  primaryNumRefinements: PropTypes.string,
  children: PropTypes.node.isRequired,
  buttonProps: PropTypes.shape({
    viewMoreButtonProps: PropTypes.object,
    shouldDisplayTruncatedFilters: PropTypes.bool,
    parsedName: PropTypes.string,
    toggleExpandedItems: PropTypes.func,
  }),
};

ExpandableListWrapper.displayName = "ListWrapper";

export default ExpandableListWrapper;
